<template>
    <b-container fluid>
        <div class="d-flex align-items-center">
            <h6 class="my-3 mr-2">Guest Verification</h6>
            <form-switch @change="showConfirmation" v-model="values.guest_verification_enabled" />
        </div>
        <div class="pl-4 flex-column align-items-center">
            <h6 class="my-3" v-if="values.guest_verification_enabled">Settings</h6>
            <b-form-group class="my-3" v-if="values.guest_verification_enabled">
                <form-switch @change="saveSettings" v-model="values.guest_verification_id_only" />
                <label>Disable Selfie Check (recommended).</label>
                <br />
                <small>
                    Comparing selfie image to ID image is prone to higher failures as distorted images are common with
                    guest submitted selfies.
                </small>
            </b-form-group>

            <h6 class="my-3" v-if="values.guest_verification_enabled">Notifications</h6>
            <b-form-group v-if="values.guest_verification_enabled">
                <form-switch @change="saveSettings" v-model="values.guest_verification_notification_email_default" />
                <label
                    >Use <b>{{ userEmail }}</b></label
                >
                <form-group-email
                    v-if="values.guest_verification_enabled && !values.guest_verification_notification_email_default"
                    v-model="values.guest_verification_notification_email_alt"
                    @input="saveNotificationEmail"
                    label="E-mail address for notifications"
                    class="mt-2"
                />
            </b-form-group>

            <b-form-group v-if="values.guest_verification_enabled">
                <form-switch @change="saveSettings" v-model="values.guest_verification_notification_verified_enabled" />
                <label>Successful Guest Verification</label>
            </b-form-group>
        </div>
        <modal
            size="lg"
            title="Confirm Changes"
            ok-title="Confirm"
            @cancel="cancel"
            :ok-disabled="!agreeToTerms"
            :id="modalId"
            @ok="setEnabled(true)"
            @close="cancel"
            no-close-on-backdrop
            no-close-on-esc
        >
            <p>
                Guest Verify is a fee per use service. Carefully read details below and agree to the terms or service.
            </p>
            <ul>
                <li>
                    Once enabled a guest will be required to upload a picture ID (drivers license, state ID, etc.). This
                    is done by the guest after the booking is complete.
                </li>
                <li>
                    A guest will also be required to upload a selfie to match the selfie picture against the image on
                    the picture ID.
                </li>
                <li>Each guest verification, whether it succeeds or fails, will cost USD $1.00.</li>
                <li>
                    Billing will occur monthly, at the same time billing occurs for the Lodgix monthly subscription.
                </li>
                <li>
                    Detailed guest verification data for each billing period is accessible from within the
                    <i>settings > billing profile</i> area of Lodgix.
                </li>
                <li>Trial period users will only be billed for trial period usage IF they become PAID subscribers.</li>
                <li>
                    Additional details can be found in our
                    <a
                        class="link"
                        href="https://support.lodgix.com/en/articles/7172754-guest-verification-module"
                        target="_blank"
                        >support documentation</a
                    >.
                </li>
            </ul>
            <form-checkbox v-model="agreeToTerms">Agree to terms.</form-checkbox>
        </modal>
    </b-container>
</template>

<script>
    import FormSwitch from '../../generic/form-switch/form-switch.vue';
    import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';

    import FormGroupEmail from '../../generic/form-group-email/form-group-email.vue';
    import { formatResponseError } from '../../../utils/api';
    import api from '../../../api';
    import { GUEST_VERIFICATION_SETTINGS_ENDPOINT } from '../../../endpoints';
    import modal from '../../generic/modal/modal.vue';
    import { mapGetters } from 'vuex';
    export default {
        naem: 'GuestVerificationSetup',
        components: {
            FormSwitch,
            FormGroupEmail,
            FormCheckbox,
            modal,
        },
        data() {
            return {
                agreeToTerms: false,
                values: {
                    guest_verification_enabled: false,
                    guest_verification_notification_verified_enabled: false,
                    guest_verification_notification_email_default: true,
                    guest_verification_notification_email_alt: '',
                    guest_verification_id_only: false,
                },
                modalId: 'confirm-verification-modal',
            };
        },
        created() {
            this.fetchData();
        },
        computed: {
            ...mapGetters('header', ['userEmail']),
        },
        methods: {
            cancel() {
                this.values.guest_verification_enabled = false;
            },
            async fetchData() {
                try {
                    this.$parent.$emit('handleBusy', true);
                    const { data } = await api.get(GUEST_VERIFICATION_SETTINGS_ENDPOINT());
                    this.values = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not fetch settings',
                    });
                } finally {
                    this.$parent.$emit('handleBusy', false);
                }
            },
            showConfirmation(obj) {
                if (obj.value) {
                    this.$bvModal.show(this.modalId);
                } else {
                    this.setEnabled(false);
                }
            },
            saveNotificationEmail(value) {
                this.saveSettings(value);
            },
            async setEnabled(value) {
                try {
                    this.$parent.$emit('handleBusy', true);
                    await api.post(`${GUEST_VERIFICATION_SETTINGS_ENDPOINT()}set-enabled/`, {
                        guest_verification_enabled: value,
                    });
                    this.values.guest_verification_enabled = value;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not save settings',
                    });
                } finally {
                    this.$parent.$emit('handleBusy', false);
                }
            },
            async saveSettings(emailValue = '') {
                try {
                    this.$parent.$emit('handleBusy', true);
                    const { data } = await api.post(GUEST_VERIFICATION_SETTINGS_ENDPOINT(), {
                        ...this.values,
                        guest_verification_notification_email_alt: this.values
                            .guest_verification_notification_email_default
                            ? null
                            : emailValue,
                    });
                    this.values = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not save settings',
                    });
                } finally {
                    this.$parent.$emit('handleBusy', false);
                }
            },
        },
    };
</script>

<style></style>
